/* eslint-disable no-debugger */

import React, { ChangeEvent, FC, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Editor } from 'react-draft-wysiwyg';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import CustomReference from 'components/CustomReference';
import InfoIcon from '@material-ui/icons/Info';
import { useContainer } from 'unstated-next';
import { JobCreateContainer } from '../providers/job-create';
import { ImportedJobContainer } from '../providers/imported-job';
import { Show } from 'components/Show';
import { getFieldValidation, getFilterBuilding } from '../utils';
import { JobCreateFormFields } from '../types';
import { TRIM_OPTION_RESET } from '../constants';
import { modelDefaultsToJobFields } from '../model-defaults';
import { CreateSubdivisionInput } from './CreateSubdivisionInput';
import { AutoCompleteReference } from 'components/AutoCompleteReference/AutoCompleteReference';
import Checkbox from '@material-ui/core/Checkbox';
import ConfirmDialog from 'components/ConfirmDialog';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import { PhaseStructureTypeEnum, PhaseTypeEnum } from '@vatos-pas/common';
import { AddressEditor, AddressEditorField } from './address-editor';
import { PaymentOptionCell, PaymentOptionsGrid } from './payment-option-cell';

const ZIP_CODE_REGEX = new RegExp(/^\d{5}(-\d{4})?$/);

type FieldsPayload = Partial<JobCreateFormFields>;

export const ProjectDetailsCreate: FC = () => {
  const {
    setFields,
    errors,
    fields,
    options,
    setOptions,
    toggleBuilding,
    setToggleBuilding,
    buildingId,
    setBuildingId,
    buildingName,
    setBuildingName,
    subdivisions,
    phases,
    loadingPhases,
    applyParsedPlace,
    updateCoordinates,
  } = useContainer(JobCreateContainer);
  const [openConfirmModelChange, setOpenConfirmModelChange] = useState(false);
  const [openPhaseConfirm, setOpenPhaseConfirm] = useState(false);
  const [pendingPhaseId, setPendingPhaseId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState();
  const [previousModel, setPreviousModel] = useState<any>();

  const { importedJob } = useContainer(ImportedJobContainer);

  const classes = useStyles();

  const ComponentIconBuilding = toggleBuilding
    ? AddCircleIcon
    : RemoveCircleIcon;

  const showImported =
    !!importedJob?.modelId ||
    !!importedJob?.subdivisionId ||
    !!importedJob?.builderId;

  const onChangeFields =
    (currentField: keyof JobCreateFormFields, payload?: FieldsPayload) =>
    (
      event: ChangeEvent<{
        name?: string | undefined;
        value: unknown;
      }>,
    ) => {
      if (event.target.value === fields[currentField]) return;

      setFields(prevState => ({
        ...prevState,
        [currentField]: event.target.value,
        ...payload,
      }));
    };

  const onChangeFieldCheckBox =
    (currentField: keyof JobCreateFormFields, payload?: FieldsPayload) =>
    (event: any) => {
      if (event.target.checked === fields[currentField]) return;

      setFields(prevState => ({
        ...prevState,
        [currentField]: event.target.checked,
        ...payload,
      }));
    };

  const onChangeFieldsNoEvent =
    (currentField: keyof JobCreateFormFields, payload?: FieldsPayload) =>
    (value: any) => {
      if (!value) {
        return setFields(prevState => ({
          ...prevState,
          [currentField]: '',
          ...payload,
        }));
      }
      if (value.id === fields[currentField]) return;
      setFields(prevState => ({
        ...prevState,
        [currentField]: value.id,
        ...payload,
      }));
    };

  const onChangeModel =
    (currentField: keyof JobCreateFormFields, payload?: FieldsPayload) =>
    (value: any) => {
      setPreviousModel(value);
      setOpenConfirmModelChange(false);
      if (!value) {
        return setFields(prevState => ({
          ...prevState,
          [currentField]: '',
          interiorOptionCost: 0,
          exteriorOptionCost: 0,
          ...TRIM_OPTION_RESET,
          ...payload,
        }));
      }
      if (value.id === fields[currentField]) return;
      setFields(prevState => ({
        ...prevState,
        [currentField]: value.id,
        ...modelDefaultsToJobFields(value),
        ...payload,
      }));
    };

  const getModelFilter = () => {
    return fields.subdivision
      ? { subdivisionId: fields.subdivision, active: true }
      : { active: true };
  };

  // Check if selected subdivision is DrywallAndPaint
  const selectedSubdivision = subdivisions?.find(
    sub => sub.id === fields.subdivision,
  );
  const isDrywallAndPaint =
    selectedSubdivision?.phaseStructureType ===
    PhaseStructureTypeEnum.DrywallAndPaint;

  // Phase dropdown options — hide Trim phases when hasTrim=false
  const phaseOptions = phases.filter(phase => {
    if (
      !fields.hasTrim &&
      (phase.phaseType === PhaseTypeEnum.Trim1 ||
        phase.phaseType === PhaseTypeEnum.Trim2)
    ) {
      return false;
    }
    return true;
  });

  const lastPhaseId = phaseOptions.length
    ? phaseOptions[phaseOptions.length - 1].id
    : null;

  const materialsPhase = phases.find(
    p => p.phaseType === PhaseTypeEnum.Materials,
  );

  const garagePhase = phases.find(p => p.phaseType === PhaseTypeEnum.Garage);
  const isGaragePhase = !!(
    garagePhase && fields.startingPhaseId === garagePhase.id
  );

  // Composite select value: last phase has two entries (normal + approved)
  // Use "{id}:approved" to distinguish the approved variant
  const APPROVED_SUFFIX = ':approved';

  const getSelectValue = () => {
    if (!fields.startingPhaseId) return '';
    if (
      fields.startingPhaseId === lastPhaseId &&
      fields.startingPhaseApproved
    ) {
      return fields.startingPhaseId + APPROVED_SUFFIX;
    }
    return fields.startingPhaseId;
  };

  const handlePhaseChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const rawValue = event.target.value as string;
    const isApprovedOption = rawValue.endsWith(APPROVED_SUFFIX);
    const phaseId = isApprovedOption
      ? rawValue.slice(0, -APPROVED_SUFFIX.length)
      : rawValue;
    const isMaterials = materialsPhase && phaseId === materialsPhase.id;

    if (!isMaterials) {
      setPendingPhaseId(rawValue);
      setOpenPhaseConfirm(true);
    } else {
      applyPhaseSelection(rawValue);
    }
  };

  const getApprovedStatus = (
    isApproved: boolean,
    phaseId: string,
    lastId: string | null,
  ): boolean | undefined => {
    if (isApproved) return true;
    if (phaseId === lastId) return false;
    return undefined;
  };

  const applyPhaseSelection = (rawValue: string) => {
    const isApprovedOption = rawValue.endsWith(APPROVED_SUFFIX);
    const phaseId = isApprovedOption
      ? rawValue.slice(0, -APPROVED_SUFFIX.length)
      : rawValue;
    setFields(prev => ({
      ...prev,
      startingPhaseId: phaseId,
      startingPhaseApproved: getApprovedStatus(
        isApprovedOption,
        phaseId,
        lastPhaseId,
      ),
      ...(garagePhase && phaseId === garagePhase.id && { garageFirst: true }),
    }));
  };

  const handlePhaseConfirm = () => {
    if (pendingPhaseId) {
      applyPhaseSelection(pendingPhaseId);
    }
    setPendingPhaseId(null);
    setOpenPhaseConfirm(false);
  };

  const handlePhaseCancel = () => {
    setPendingPhaseId(null);
    setOpenPhaseConfirm(false);
  };

  const handleTrimChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    const isUnchecking = !(event.target as HTMLInputElement).checked;
    onChangeFieldCheckBox(
      'hasTrim',
      isUnchecking ? TRIM_OPTION_RESET : undefined,
    )(event);
    if (isUnchecking && fields.startingPhaseId) {
      const currentPhase = phases.find(p => p.id === fields.startingPhaseId);
      if (
        currentPhase &&
        (currentPhase.phaseType === PhaseTypeEnum.Trim1 ||
          currentPhase.phaseType === PhaseTypeEnum.Trim2)
      ) {
        const matPhase = phases.find(
          p => p.phaseType === PhaseTypeEnum.Materials,
        );
        if (matPhase) {
          setFields(prev => ({
            ...prev,
            startingPhaseId: matPhase.id,
            startingPhaseApproved: undefined,
          }));
        }
      }
    }
  };

  const handleTrimOptionChange = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const isUnchecking = !(event.target as HTMLInputElement).checked;
    onChangeFieldCheckBox(
      'hasTrimOptions',
      isUnchecking ? { trimOptionCost: undefined } : undefined,
    )(event);
  };

  const confirmModelOpen = value => {
    if (selectedModel) {
      setOpenConfirmModelChange(true);
    } else {
      onChangeModel('model')(value);
    }
    setSelectedModel(value);
  };

  const handleAddressFieldChange = (
    field: AddressEditorField,
    value: string,
  ) => {
    if (value === fields[field]) return;
    setFields(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  const addressDisabledFields: Partial<Record<AddressEditorField, boolean>> = {
    address1: !!importedJob?.spOrderJobStreet,
    address2: !!importedJob?.spOrderJobStreetSupplement,
    city: !!importedJob?.spOrderJobCity,
    state: importedJob?.spOrderJobStateCode === 'USFL',
    zip: !!importedJob?.spOrderJobPostalCode?.match(ZIP_CODE_REGEX),
  };

  return (
    <Box width="100%">
      <Box className={classes.grayBox}>
        <Typography variant="body1" gutterBottom className={classes.bold}>
          Project Details
        </Typography>
        <Box
          width="100%"
          display="flex"
          mt={2}
          className={classes.projectDetailsRow}
        >
          <Box className={classes.projectDetailsLeft}>
            <Box>
              <Show condition={!importedJob || !!fields.builder}>
                <AutoCompleteReference
                  label="Builder"
                  reference="builder"
                  source=""
                  sort={{ field: 'name', order: 'ASC' }}
                  filter={{}}
                  value={fields.builder}
                  disabled={showImported || (!!fields.builder && !!importedJob)}
                  onChange={item => {
                    onChangeFieldsNoEvent('builder', {
                      subdivision: '',
                      model: '',
                    })(item);
                    setPreviousModel(null);
                  }}
                />
              </Show>
              <Show condition={!!importedJob && !fields.builder}>
                <Box
                  display="flex"
                  borderBottom="1px dotted gray"
                  marginBottom="14px"
                  marginTop="31px"
                >
                  <Typography>Undefined</Typography>
                  <InfoIcon color="error" />
                </Box>
              </Show>

              <Show condition={!importedJob || !!fields.subdivision}>
                <CreateSubdivisionInput
                  showImported={showImported}
                  skip={!fields.builder}
                />
              </Show>
              <Show condition={!!importedJob && !fields.subdivision}>
                <Box
                  display="flex"
                  borderBottom="1px dotted gray"
                  marginBottom="14px"
                  marginTop="46px"
                >
                  <Typography>Undefined</Typography>
                  <InfoIcon color="error" />
                </Box>
              </Show>

              <Show condition={!importedJob || !!fields.model}>
                <AutoCompleteReference
                  label="Model"
                  reference="model"
                  source=""
                  parseLabel={item => `${item.name} (${item.externalId})`}
                  sort={{ field: 'name', order: 'ASC' }}
                  value={fields.model}
                  filter={getModelFilter()}
                  skip={!fields.subdivision}
                  disabled={
                    showImported ||
                    (!!fields.model && !!importedJob) ||
                    !fields.subdivision
                  }
                  onChange={confirmModelOpen}
                  error={!!getFieldValidation(errors, 'model')}
                  helperText={getFieldValidation(errors, 'model')}
                />
              </Show>

              <Show condition={!!importedJob && !fields.model}>
                <Box
                  display="flex"
                  borderBottom="1px dotted gray"
                  marginBottom="14px"
                  marginTop="46px"
                >
                  <Typography>Undefined</Typography>
                  <InfoIcon color="error" />
                </Box>
              </Show>
            </Box>
          </Box>
          <Box className={classes.projectDetailsRight}>
            <Box
              display="flex"
              style={{ gap: 16 }}
              className={classes.responsiveRow}
            >
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-approval"
                label="Project Approval Date"
                fullWidth
                disabled
                className={classes.inputDate}
                value={null}
                onChange={date => date}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
              <KeyboardDatePicker
                disableToolbar
                className={classes.inputDate}
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-due"
                label="Project Due Date"
                fullWidth
                disabled
                value={null}
                onChange={date => date}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Box>
            <TextField
              className={classes.input}
              fullWidth
              label="Job ID #"
              disabled={!!importedJob?.spOrderBuilderOrderNumber}
              value={fields.idNumber}
              onChange={onChangeFields('idNumber')}
              error={!!getFieldValidation(errors, 'idNumber')}
              helperText={getFieldValidation(errors, 'idNumber')}
            />
          </Box>
        </Box>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          className={classes.responsiveRow}
        >
          <Show condition={toggleBuilding}>
            <CustomReference
              value={buildingId ?? undefined}
              label="Building"
              resource="building"
              sort={{ field: 'number', order: 'ASC' }}
              customItemName="number"
              disabled={!fields.subdivision}
              needsFilter
              perPage={100}
              onChange={event => {
                setBuildingId(event.target.value as string);
              }}
              filters={getFilterBuilding(fields.subdivision)}
              error={!!getFieldValidation(errors, 'building')}
              helperText={getFieldValidation(errors, 'building')}
            />
          </Show>
          <Show condition={!toggleBuilding}>
            <TextField
              label="Building #"
              fullWidth
              disabled={!fields.subdivision}
              className={classes.input}
              value={buildingName}
              onChange={event => setBuildingName(event.target.value)}
              error={!!getFieldValidation(errors, 'building')}
              helperText={getFieldValidation(errors, 'building')}
            />
          </Show>

          <ComponentIconBuilding
            titleAccess="Add new Building"
            className={classes.pointer}
            onClick={() => setToggleBuilding(!toggleBuilding)}
          />
          <TextField
            label="Lot"
            value={fields.lot}
            fullWidth
            disabled={!!importedJob?.spOrderJobLot}
            className={classes.inputNoMarginRight}
            onChange={onChangeFields('lot')}
          />
        </Box>
        <Box
          width="100%"
          display="flex"
          style={{ gap: 16 }}
          className={classes.responsiveRow}
        >
          <TextField
            label="Block"
            fullWidth
            className={classes.input}
            disabled={!!importedJob && !importedJob.spOrderJobBlock}
            value={fields.block ?? ''}
            onChange={onChangeFields('block')}
            error={!!getFieldValidation(errors, 'block')}
            helperText={getFieldValidation(errors, 'block')}
          />
          <TextField
            label="Community Phase"
            fullWidth
            className={classes.input}
            value={fields.communityPhase}
            onChange={onChangeFields('communityPhase')}
            error={!!getFieldValidation(errors, 'communityPhase')}
            helperText={getFieldValidation(errors, 'communityPhase')}
          />
        </Box>
        <AddressEditor
          values={{
            address1: fields.address1,
            address2: fields.address2,
            city: fields.city,
            state: fields.state,
            zip: fields.zip,
            latitude: fields.latitude,
            longitude: fields.longitude,
          }}
          onFieldChange={handleAddressFieldChange}
          onPlaceSelected={applyParsedPlace}
          onCoordinatesChange={updateCoordinates}
          disabledFields={addressDisabledFields}
          errors={errors}
        />
        <Box mt={2}>
          <Typography>Supervisors</Typography>
        </Box>
        <Box display="flex" flexWrap="wrap">
          {fields.supervisors.map(item => (
            <Chip
              style={{ margin: '5px 5px' }}
              key={item}
              label={item.name}
            />
          ))}
        </Box>
        <Box
          width="100%"
          display="flex"
          style={{ gap: 16 }}
          className={classes.responsiveRow}
        >
          <TextField
            label="Total Square Footage"
            fullWidth
            className={classes.input}
            value={fields.totalSquareFootage || ''}
            onChange={onChangeFields('totalSquareFootage')}
            error={!!getFieldValidation(errors, 'totalSquareFootage')}
            helperText={getFieldValidation(errors, 'totalSquareFootage')}
            type="number"
            inputProps={{ step: '0.01', min: '0' }}
          />
          <TextField
            label="Geofencing Threshold (ft)"
            fullWidth
            className={classes.input}
            value={fields.geoDistanceTolerance ?? ''}
            disabled
            type="number"
            InputLabelProps={{ shrink: !!fields.geoDistanceTolerance }}
          />
        </Box>
        <FormControl fullWidth className={classes.input}>
          <InputLabel>Job Start Phase</InputLabel>
          <Select
            value={getSelectValue()}
            onChange={handlePhaseChange}
            disabled={!fields.subdivision || loadingPhases}
          >
            {loadingPhases ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              phaseOptions.flatMap(phase =>
                phase.id === lastPhaseId
                  ? [
                      <MenuItem key={phase.id} value={phase.id}>
                        {phase.name}
                      </MenuItem>,
                      <MenuItem
                        key={phase.id + APPROVED_SUFFIX}
                        value={phase.id + APPROVED_SUFFIX}
                      >
                        {phase.name} (Approved)
                      </MenuItem>,
                    ]
                  : [
                      <MenuItem key={phase.id} value={phase.id}>
                        {phase.name}
                      </MenuItem>,
                    ],
              )
            )}
          </Select>
        </FormControl>
        <Box mt={2}>
          <Box>
            <Typography className={classes.bold}>
              Additional Workflow Options
            </Typography>
          </Box>
          <Box display="flex">
            <Box display="flex" alignItems="center" mr={2}>
              <Checkbox
                checked={fields.garageFirst}
                onClick={event => onChangeFieldCheckBox('garageFirst')(event)}
                disabled={isGaragePhase}
              />
              <Typography>Garage First</Typography>
            </Box>
            {isDrywallAndPaint && (
              <Box display="flex" alignItems="center">
                <Checkbox checked={fields.hasTrim} onClick={handleTrimChange} />
                <Typography>Trim</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box mt={2}>
          <Box>
            <Typography className={classes.bold}>
              Additional Payment Options
            </Typography>
          </Box>
          <PaymentOptionsGrid>
            <PaymentOptionCell
              label="High Ceiling"
              checked={fields.highCeiling}
              onToggle={onChangeFieldCheckBox('highCeiling')}
            />
            <PaymentOptionCell
              label="Screw Inspection"
              checked={fields.screwInspection}
              onToggle={onChangeFieldCheckBox('screwInspection')}
            />
            {isDrywallAndPaint && (
              <>
                <PaymentOptionCell
                  label="Paint Interior Options"
                  checked={fields.hasInteriorOptions}
                  onToggle={onChangeFieldCheckBox('hasInteriorOptions')}
                >
                  <TextField
                    fullWidth
                    label="Interior Option Cost"
                    disabled={!fields.hasInteriorOptions}
                    value={fields.interiorOptionCost}
                    onChange={onChangeFields('interiorOptionCost')}
                    error={!!getFieldValidation(errors, 'interiorOptionCost')}
                    helperText={getFieldValidation(errors, 'interiorOptionCost')}
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </PaymentOptionCell>
                <PaymentOptionCell
                  label="Paint Exterior Options"
                  checked={fields.hasExteriorOptions}
                  onToggle={onChangeFieldCheckBox('hasExteriorOptions')}
                >
                  <TextField
                    fullWidth
                    label="Exterior Option Cost"
                    disabled={!fields.hasExteriorOptions}
                    value={fields.exteriorOptionCost}
                    onChange={onChangeFields('exteriorOptionCost')}
                    error={!!getFieldValidation(errors, 'exteriorOptionCost')}
                    helperText={getFieldValidation(errors, 'exteriorOptionCost')}
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </PaymentOptionCell>
                <PaymentOptionCell
                  label="Trim Option"
                  checked={!!fields.hasTrimOptions}
                  disabled={!fields.hasTrim}
                  onToggle={handleTrimOptionChange}
                >
                  <TextField
                    fullWidth
                    label="Trim Option Cost"
                    disabled={!fields.hasTrimOptions}
                    value={fields.trimOptionCost ?? ''}
                    onChange={onChangeFields('trimOptionCost')}
                    error={!!getFieldValidation(errors, 'trimOptionCost')}
                    helperText={getFieldValidation(errors, 'trimOptionCost')}
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    inputProps={{ step: '0.01', min: '0.01' }}
                  />
                </PaymentOptionCell>
              </>
            )}
          </PaymentOptionsGrid>
        </Box>
      </Box>
      <Box className={classes.grayBox}>
        <Typography variant="body1" gutterBottom className={classes.bold}>
          Options
        </Typography>
        <Box>
          <Editor
            editorState={options}
            editorClassName={classes.editor}
            toolbar={{
              options: ['inline', 'blockType', 'list'],
              fontSize: {
                options: [12, 14, 16, 18, 24, 30],
              },
              inline: {
                options: ['bold', 'italic', 'underline'],
              },
              blockType: {
                inDropdown: false,
                options: ['Normal', 'H1', 'H2', 'H3'],
              },
              list: {
                options: ['unordered', 'ordered'],
              },
            }}
            onEditorStateChange={value => setOptions(value)}
          />
        </Box>
      </Box>
      <Show condition={!!importedJob}>
        <Box className={classes.grayBox}>
          <Typography variant="body1" gutterBottom className={classes.bold}>
            Other SupplyPro Information
          </Typography>
          <Box>
            <TextField
              className={classes.inputMarginRight}
              fullWidth
              label="Group Description"
              disabled
              value={importedJob?.spOrderJobGroupDescription}
            />
          </Box>
        </Box>
      </Show>
      <ConfirmDialog
        open={openConfirmModelChange}
        title={`If you choose a different Model, all your changes will be lost. Do you want to continue?`}
        handleClose={() => {
          setSelectedModel(previousModel);
          onChangeModel('model')(previousModel);
        }}
        onConfirm={() => onChangeModel('model')(selectedModel)}
      />
      <ConfirmDialog
        open={openPhaseConfirm}
        showWarning
        title="Selecting any phase other than Materials will skip the Purchase Order Sign-Off. Are you sure you want to continue?"
        handleClose={handlePhaseCancel}
        onConfirm={handlePhaseConfirm}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  responsiveRow: {
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  projectDetailsRow: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  projectDetailsLeft: {
    width: '50%',
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
    },
  },
  projectDetailsRight: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  input: {
    margin: '8px 0px',
  },
  inputDate: {
    margin: '0px 0px 15px',
  },
  inputNoMarginRight: {
    margin: '8px 0px 8px 15px',
  },
  inputMarginRight: {
    margin: '15px 15px 15px 0px',
  },
  grayBox: {
    border: '1.5px solid lightgray',
    borderRadius: '10px',
    padding: '25px',
    marginBottom: '5px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  button: {
    marginLeft: 'auto',
    marginRight: 20,
    backgroundColor: '#4caf51',
    color: 'white',
    '&:hover': {
      backgroundColor: '#4caf51',
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  pointer: {
    cursor: 'pointer',
  },
  editor: {
    border: '1px solid gray',
    padding: '3px',
  },
  box: {
    marginBottom: '15px',
    width: '100%',
    marginTop: '8px',
  },
}));
